
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { getFullImageUrl } from "@/lib/utils";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SlabEditImageProps {
  imageUrl: string;
  name: string;
  onImageChange: (newImageUrl: string) => void;
}

export function SlabEditImage({ imageUrl, name, onImageChange }: SlabEditImageProps) {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('slab-images')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('slab-images')
        .getPublicUrl(fileName);

      if (urlData.publicUrl) {
        onImageChange(urlData.publicUrl);
        toast.success("Imagem carregada com sucesso!");
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      toast.error("Erro ao fazer upload da imagem");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!imageUrl) return;

    try {
      // Se a URL for completa, extrair apenas o nome do arquivo
      const fileName = imageUrl.includes('/')
        ? imageUrl.split('/').pop()
        : imageUrl;

      if (!fileName) {
        throw new Error('Nome do arquivo inválido');
      }

      const { error } = await supabase.storage
        .from('slab-images')
        .remove([fileName]);

      if (error) throw error;

      onImageChange('');
      toast.success("Imagem removida com sucesso!");
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      toast.error("Erro ao remover imagem");
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="image">Image</Label>
      <div className="flex flex-col gap-4">
        <div className="relative">
          <img
            src={getFullImageUrl(imageUrl)}
            alt={name}
            className="w-full max-h-48 object-cover rounded-lg"
          />
          {imageUrl && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remover imagem</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação não pode ser desfeita. A imagem será removida permanentemente do sistema.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteImage}>
                    Remover
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        <Input
          id="image"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </div>
    </div>
  );
}
